import { useState } from "react"
import { Box, Button, DialogActions, DialogContent, DialogTitle } from "@mui/material"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import Iconify from "minimals-template/components/Iconify"
import { useDialog } from "lib/@hooks/useDialog"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { getSystemDemands } from "lib/permissions/demands"
import { getScheduleTypes } from "routes/schedule/plugins/schedule-types/controller/schedule-types.api"
import { getAvailableFeatures } from "lib/features"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { buildFeaturePackExport, getFeaturePackFileName } from "../auth-bundle-feature-pack"
import { AuthBundleHeader } from "./auth-bundle-header"
import { AuthBundleDemands } from "./auth-bundle-demands"
import { FeaturePackImportDialog } from "./feature-pack-import-dialog"

export function AuthBundleDialog({ bundle = {}, ok, cancel }) {
    const demands = getSystemDemands.useResults({}, withInitialValue(IS_LOADING))
    const scheduleTypes = getScheduleTypes.useResults({}, withInitialValue(IS_LOADING))
    const features = getAvailableFeatures() || []
    const [target, setTarget] = useState(createInitialTarget)
    const openImportDialog = useDialog(<FeaturePackImportDialog />, { maxWidth: "sm", fullWidth: true })

    const demandOptions = buildDemandOptions()
    const scheduleTypeOptions = buildScheduleTypeOptions()
    const featureOptions = buildFeatureOptions()
    const demandItems = buildDemandItems()
    const scheduleTypeItems = buildScheduleTypeItems()
    const featureItems = buildFeatureItems()
    const totalDemands = demandItems.length + scheduleTypeItems.length + featureItems.length
    const canSave = Boolean(target.name?.trim())

    if (demands === IS_LOADING || scheduleTypes === IS_LOADING) {
        return (
            <Box sx={{ width: { xs: "100vw", sm: 720 }, maxWidth: 1 }}>
                <DialogTitle>Auth Bundle</DialogTitle>
                <DialogContent>
                    <LoadingScreen />
                </DialogContent>
            </Box>
        )
    }

    return (
        <Box sx={{ width: { xs: "100vw", sm: 720 }, maxWidth: 1 }} data-cy="auth-bundle-editor">
            <DialogTitle sx={{ pb: 1 }}>Auth Bundle Editor</DialogTitle>
            <DialogContent sx={{ bgcolor: "background.neutral", py: 2.5 }}>
                <AuthBundleHeader
                    bundleName={target.name}
                    demandCount={demandItems.length}
                    scheduleTypeCount={scheduleTypeItems.length}
                    featureCount={featureItems.length}
                    totalDemands={totalDemands}
                    onNameChange={handleNameChange}
                    onExport={handleExportPack}
                    onImport={handleImportPack}
                />
                <AuthBundleDemands
                    demandItems={demandItems}
                    scheduleTypeItems={scheduleTypeItems}
                    featureItems={featureItems}
                    demandOptions={demandOptions}
                    scheduleTypeOptions={scheduleTypeOptions}
                    featureOptions={featureOptions}
                    onAddDemand={handleAddDemand}
                    onAddScheduleType={handleAddScheduleType}
                    onAddFeature={handleAddFeature}
                    onRemoveDemand={handleRemoveDemand}
                    onRemoveScheduleType={handleRemoveScheduleType}
                    onRemoveFeature={handleRemoveFeature}
                    onImport={handleImportPack}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={cancel} color="primary" data-cy="dialogActionsCancelButton">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={!canSave}
                    variant="contained"
                    startIcon={<Iconify icon="eva:checkmark-fill" />}
                    data-cy="dialogActionsOkButton"
                >
                    Save Bundle
                </Button>
            </DialogActions>
        </Box>
    )

    function createInitialTarget() {
        return {
            name: bundle.name || "",
            demands: [...(bundle.demands || [])],
            scheduleTypes: [...(bundle.scheduleTypes || [])],
            features: [...(bundle.features || [])],
        }
    }

    function buildDemandOptions() {
        if (!Array.isArray(demands)) return []
        return demands.filter(isUnselectedDemand)
    }

    function buildScheduleTypeOptions() {
        if (!Array.isArray(scheduleTypes)) return []
        return scheduleTypes.filter(isUnselectedScheduleType)
    }

    function buildFeatureOptions() {
        return features.filter(isUnselectedFeature)
    }

    function buildDemandItems() {
        return (target.demands || []).map(createDemandItem)
    }

    function buildScheduleTypeItems() {
        return (target.scheduleTypes || []).map(createScheduleTypeItem)
    }

    function buildFeatureItems() {
        return (target.features || []).map(createFeatureItem)
    }

    function createDemandItem(value) {
        return { value, label: value, type: "permission" }
    }

    function createScheduleTypeItem(value) {
        return { value, label: value, type: "scheduleType" }
    }

    function createFeatureItem(value) {
        const id = featureIdFromStoredValue(value)
        return { value, label: `feature-${id}`, type: "feature" }
    }

    function isUnselectedDemand(option) {
        return !target.demands?.includes(option?.name)
    }

    function isUnselectedScheduleType(option) {
        return !target.scheduleTypes?.includes(getScheduleTypeLabel(option))
    }

    function isUnselectedFeature(option) {
        return !target.features?.includes(getFeatureLabel(option))
    }

    function handleNameChange(event) {
        const name = event.target.value
        setTarget((current) => ({ ...current, name }))
    }

    function handleAddDemand(option) {
        if (!option?.name) return
        addValue("demands", option.name)
    }

    function handleAddScheduleType(option) {
        if (!option?.id) return
        addValue("scheduleTypes", getScheduleTypeLabel(option))
    }

    function handleAddFeature(option) {
        if (!option?.id) return
        addValue("features", getFeatureLabel(option))
    }

    function handleRemoveDemand(value) {
        removeValue("demands", value)
    }

    function handleRemoveScheduleType(value) {
        removeValue("scheduleTypes", value)
    }

    function handleRemoveFeature(value) {
        removeValue("features", value)
    }

    async function handleImportPack() {
        const imported = await openImportDialog({
            current: target.features || [],
            features,
        })
        if (!imported) return
        setTarget((current) => ({ ...current, features: imported }))
        successSnackbar("Feature pack applied to bundle")
    }

    function handleExportPack() {
        const pack = buildFeaturePackExport(target)
        downloadJson(pack, getFeaturePackFileName(target.name))
        successSnackbar("Feature pack exported")
    }

    function handleSave() {
        if (!canSave) return
        ok(target)
    }

    function addValue(field, value) {
        setTarget((current) => {
            const existing = current[field] || []
            if (existing.includes(value)) return current
            return { ...current, [field]: [...existing, value] }
        })
    }

    function removeValue(field, value) {
        setTarget((current) => ({
            ...current,
            [field]: (current[field] || []).filter((item) => item !== value),
        }))
    }

    function getScheduleTypeLabel(option) {
        return `${option?.name} [${option?.id}]`
    }

    function getFeatureLabel(option) {
        return `${option?.name} [${option?.id}]`
    }

    function featureIdFromStoredValue(value) {
        const match = String(value || "").match(/\[([^\]]+)\]$/)
        if (match) return match[1]
        return String(value || "").replace(/^feature-/, "")
    }

    function downloadJson(value, fileName) {
        const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement("a")
        anchor.href = url
        anchor.download = fileName
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
    }
}
